import React, { useCallback, useEffect, useState, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  Close,
  LocalAtm as LocalAtmIcon,
  ArrowBack as ArrowBackIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import GridContainer from "../../components/Grid/GridContainer.js";
import GridItem from "../../components/Grid/GridItem.js";
import CustomInput from "../../components/CustomInput/CustomInput.js";
import Button from "../../components/CustomButtons/Button.js";
import Card from "../../components/Card/Card.js";
import CardHeader from "../../components/Card/CardHeader.js";
import CardIcon from "../../components/Card/CardIcon.js";
import CardBody from "../../components/Card/CardBody.js";
import Snackbars from "../../components/Snackbar/Snackbar.js";
import { AutocompleteComponent } from "../../components/Autocomplete/AutocompleteComponent";
import { convertToMapById } from "../../helpers/convertToMap";
import { useColorTheme } from "../../hooks/useColorTheme";
import { useForm } from "../../hooks/useForm";
import { setError, clearError } from "../../redux/ducks/newBrandStation";
import {
  addNewBrandStationV2,
  editNewBrandStationV2,
} from "../../redux/actions/brands/brandsAction_V2";
import { TitleWrapperStyled, TitleStyled, styles } from "./styles";

const useStyles = makeStyles(styles);

function NewBrandStation() {
  const { isEdit, id } = useParams();
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.login);
  const colorBg = useSelector((state) => state.config.colorBg);
  const stations = useSelector((state) => state.companies.stations);
  const { brandName, brands } = useSelector((state) => state.brandsReducer);
  const [mapBrands, setMapBrands] = useState([]);
  const [mapStations, setMapStations] = useState([]);
  const fileInputRef = useRef();

  const handleSaveImagesButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    setMapBrands(convertToMapById(brandName));
  }, [brandName]);

  useEffect(() => {
    setMapStations(convertToMapById(stations));
  }, [stations]);

  const error = useSelector(
    (state) => state.newBrandStation.error,
    shallowEqual,
  );
  const errorMessage = useSelector(
    (state) => state.newBrandStation.errorMessage,
    shallowEqual,
  );

  const { color } = useColorTheme(colorBg);
  const classes = useStyles();
  let history = useHistory();
  const { inputValues, handleInputOnChange, handleNewValues, setValues } = useForm({
    name: "",
    alias: "",
    servicePhone: "",
    leakPhone: "",
    creLicense: "",
    address: "",
    idStation: "",
    logo: null,
  });

  const { name, alias, servicePhone, leakPhone, creLicense, address, idStation, logo } = inputValues;
  const [aliasState, setAliasState] = React.useState("");

  useEffect(() => {
    setAliasState("");
  }, []);

  const sendData = useCallback((body, logo) => {
    dispatch(addNewBrandStationV2(body, token, logo));
    history.push("/admin/marcas");
  }, [dispatch]);

  const editData = useCallback((editId, body, logo) => {
    dispatch(editNewBrandStationV2(editId, body, token, logo));
    history.push("/admin/marcas");
  }, [dispatch]);

  const clearSnackbar = useCallback(() => {
    dispatch(clearError());
  }, []);

  const setSnackbarMsg = useCallback((errorMsg) => {
    dispatch(setError(errorMsg));
  }, []);

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files?.length > 0) {
      handleNewValues({ logo: files[0] });
    }
  };

  const handleSubmit = () => {
    if (!name) {
      setSnackbarMsg("El campo nombre es obligatorio.");
      return;
    }
    if (alias === "" || aliasState === "error") {
      setSnackbarMsg("El campo alias es obligatorio.");
      return;
    }

    const brand = brands[id];
    const body = {
      name: brand ? brand.name : name.label,
      alias,
      servicePhone,
      leakPhone,
      creLicense,
      address,
      idStation: idStation.value,
    };

    isEdit ? editData(id, body, logo) : sendData(body, logo);
  };

  useEffect(() => {
    isEdit && id && handleNewValues({
      ...brands[id],
      name: { ...brands[id], label: brands[id]?.name },
      idStation: stations[brands[id]?.idStation],
    });
  }, [brands, id, stations]);

  const handleGoBack = () => {
    history.goBack();
  };

  return (
    <React.Fragment>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color={color} icon>
              <CardIcon color={color}>
                <LocalAtmIcon />
              </CardIcon>
              <TitleWrapperStyled>
                <TitleStyled>
                  {isEdit ? "Editar marca" : "Nueva marca"}
                </TitleStyled>
                <Button
                  justIcon
                  round
                  color={color}
                  onClick={handleGoBack}
                >
                  <ArrowBackIcon className={classes.buttonIcon} />
                </Button>
              </TitleWrapperStyled>
            </CardHeader>
            <CardBody>
              <form>
                <InputLabel
                  htmlFor="simple-select"
                  className={classes.selectLabel}
                >
                  Nombre *
                </InputLabel>
                <FormControl fullWidth className={classes.selectFormControl}>
                  <AutocompleteComponent
                    options={mapBrands}
                    search=""
                    name="name"
                    value={name}
                    handleValues={setValues}
                    align="right"
                  />
                </FormControl>

                <CustomInput
                  success={aliasState === "success"}
                  error={aliasState === "error"}
                  labelText="Alias *"
                  id="alias"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    onChange: handleInputOnChange,
                    type: "text",
                    name: "alias",
                    value: alias,
                    endAdornment:
                      aliasState === "error" ? (
                        <InputAdornment position="end">
                          <Close className={classes.danger} />
                        </InputAdornment>
                      ) : undefined,
                  }}
                />

                <CustomInput
                  id="service_phone"
                  labelText="Telefono de servicio"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    onChange: handleInputOnChange,
                    type: "number",
                    name: "servicePhone",
                    value: servicePhone,
                  }}
                />
                <CustomInput
                  id="leak_phone"
                  labelText="Telefono de fugas"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    onChange: handleInputOnChange,
                    type: "text",
                    name: "leakPhone",
                    value: leakPhone,
                  }}
                />
                <CustomInput
                  id="cre_license"
                  labelText="Licencia"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    onChange: handleInputOnChange,
                    type: "text",
                    name: "creLicense",
                    value: creLicense,
                  }}
                />
                <CustomInput
                  id="address"
                  labelText="Dirección"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    onChange: handleInputOnChange,
                    type: "text",
                    name: "address",
                    value: address,
                  }}
                />

                <InputLabel
                  htmlFor="simple-select"
                  className={classes.selectLabel}
                >
                  Planta *
                </InputLabel>
                <FormControl fullWidth className={classes.selectFormControl}>
                  <AutocompleteComponent
                    options={mapStations}
                    search=""
                    name="idStation"
                    value={idStation}
                    handleValues={setValues}
                    align="right"
                  />
                  <Select
                    MenuProps={{ className: classes.selectMenu }}
                    classes={{ select: classes.select }}
                    value={idStation}
                    onChange={handleInputOnChange}
                    inputProps={{ name: "idStation", id: "id_station" }}
                  >
                    {mapStations &&
                      mapStations.map((value) => (
                        <MenuItem
                          key={value.id}
                          classes={{
                            root: classes.selectMenuItem,
                            selected: classes.selectMenuItemSelected,
                          }}
                          value={value.id}
                        >
                          {value.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <InputLabel htmlFor="simple-select" className={classes.selectLabel}>
                  Logo:
                </InputLabel>
                <GridContainer>
                  <GridItem xs={12} sm={2}>
                    <div style={{ position: "relative" }}>
                      <Button
                        color="info"
                        round
                        justIcon
                        tooltip="Adjuntar Logo"
                        onClick={handleSaveImagesButtonClick}
                        style={{ alignSelf: "end" }}
                      >
                        <AddPhotoAlternateIcon style={{ margin: 0 }} />
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          ref={fileInputRef}
                          onChange={handleFileChange}
                        />
                      </Button>
                    </div>
                  </GridItem>
                  <GridItem xs={12} sm={10}>
                    {logo?.name ? (
                      <Box
                        style={{
                          width: "96%",
                          alignSelf: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          border: "1px solid #ccc",
                          borderRadius: 4,
                          padding: "8px 8px",
                        }}
                      >
                        <Typography variant="body1">{logo.name}</Typography>
                        <Box style={{ alignSelf: "end" }}>
                          <Button
                            color="danger"
                            justIcon
                            round
                            size="sm"
                            tooltip="Eliminar Logo"
                            onClick={() => handleNewValues({ logo: "" })}
                          >
                            <DeleteIcon style={{ margin: 0 }} />
                          </Button>
                        </Box>
                      </Box>
                    ) : logo ? (
                      <Box>
                        <GridContainer spacing={2}>
                          <GridItem xs={12} sm={6} md={4}>
                            <img
                              src={logo}
                              alt="Logo"
                              style={{ width: "100%", height: "auto", borderRadius: 8 }}
                            />
                          </GridItem>
                          <GridItem xs={12} sm={2}>
                            <Box style={{ alignSelf: "end" }}>
                              <Button
                                color="danger"
                                justIcon
                                round
                                size="sm"
                                tooltip="Eliminar Logo"
                                onClick={() => handleNewValues({ ...inputValues, logo: null })}
                              >
                                <DeleteIcon style={{ margin: 0 }} />
                              </Button>
                            </Box>
                          </GridItem>
                        </GridContainer>
                      </Box>
                    ) : null}
                  </GridItem>
                </GridContainer>
                <div className={classes.formCategory}>
                  <small>*</small> Campos requeridos
                </div>

                <Button
                  color={color}
                  onClick={handleSubmit}
                  className={classes.registerButton}
                >
                  Guardar
                </Button>
              </form>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
      <Snackbars
        place="tr"
        color="danger"
        message={errorMessage}
        open={error}
        closeNotification={() => clearSnackbar()}
        close
      />
    </React.Fragment>
  );
}

export default React.memo(NewBrandStation);
